import {
  Button,
  mergeClasses,
  Skeleton,
  SkeletonItem,
} from "@fluentui/react-components";
import { AttachRegular, EditRegular } from "@fluentui/react-icons";
import { useDiagnosticOverviewTabStyles } from "./DiagnosticOverviewTab.styles";
import {
  IDiagnosticOverviewTabProps,
  IMetaCellProps,
} from "./DiagnosticOverviewTab.types";
import { formatDate } from "utils/generalUtils";

const SOURCE_TEMPLATE = "Template";
const SOURCE_CUSTOM = "Custom";

const MetaCell = ({ label, value, fullWidth }: IMetaCellProps): JSX.Element => {
  const styles = useDiagnosticOverviewTabStyles();
  return (
    <div className={fullWidth ? styles.gridCellFullWidth : undefined}>
      <dt className={styles.metaLabel}>{label}</dt>
      {value !== "" ? (
        <dd className={styles.metaValue}>{value}</dd>
      ) : (
        <dd className={styles.metaValueEmpty}>
          {label === "DESCRIPTION" ? "No description added." : "-"}
        </dd>
      )}
    </div>
  );
};

export const DiagnosticOverviewTab = ({
  diagnostic,
  template,
  isLoading,
  onEdit,
}: IDiagnosticOverviewTabProps): JSX.Element => {
  const styles = useDiagnosticOverviewTabStyles();

  const sourceLabel =
    diagnostic.questionBankType === "template"
      ? SOURCE_TEMPLATE
      : SOURCE_CUSTOM;

  const isTemplatedSurvey =
    diagnostic.method === "survey" &&
    diagnostic.questionBankType === "template";

  const isCustomSurveyWithUpload =
    diagnostic.method === "survey" &&
    diagnostic.questionBankType === "custom" &&
    !!diagnostic.customQuestionBankUrl;

  const createdByValue: string =
    diagnostic.createdBy?.displayName ??
    diagnostic.createdBy?.userPrincipalName ??
    "";

  if (isLoading) {
    return (
      <div className={styles.root}>
        <Skeleton>
          <div className={styles.card}>
            <SkeletonItem
              shape="rectangle"
              size={16}
              className={styles.skeletonCardTitle}
            />
            <hr className={styles.divider} />
            <dl className={styles.grid}>
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i}>
                  <SkeletonItem
                    shape="rectangle"
                    size={12}
                    className={styles.skeletonLabel}
                  />
                  <SkeletonItem
                    shape="rectangle"
                    size={16}
                    className={styles.skeletonValue}
                  />
                </div>
              ))}
              <div className={styles.gridCellFullWidth}>
                <SkeletonItem
                  shape="rectangle"
                  size={12}
                  className={styles.skeletonDescriptionLabel}
                />
                <SkeletonItem
                  shape="rectangle"
                  size={16}
                  className={styles.skeletonDescriptionValue}
                />
              </div>
            </dl>
          </div>
          <div className={styles.templateCard}>
            <SkeletonItem
              shape="rectangle"
              size={12}
              className={styles.skeletonTemplateTitle}
            />
            <SkeletonItem
              shape="rectangle"
              size={16}
              className={styles.skeletonTemplateDescLine}
            />
            <SkeletonItem
              shape="rectangle"
              size={16}
              className={styles.skeletonTemplateDescLineShort}
            />
            <div className={styles.skeletonTemplateActions}>
              <SkeletonItem
                shape="rectangle"
                size={28}
                className={styles.skeletonTemplatePrimaryButton}
              />
              <SkeletonItem
                shape="rectangle"
                size={28}
                className={styles.skeletonTemplateSecondaryButton}
              />
            </div>
          </div>
        </Skeleton>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <div className={styles.cardHeaderRow}>
          <h2 className={styles.cardHeader}>Basic information</h2>
          <Button
            appearance="subtle"
            icon={<EditRegular />}
            onClick={onEdit}
            aria-label="Edit diagnostic"
          />
        </div>
        <hr className={styles.divider} />
        <dl className={styles.grid}>
          <MetaCell label="NAME" value={diagnostic.name} />
          <MetaCell
            label="CREATED"
            value={formatDate(diagnostic.createdAt ?? undefined)}
          />
          <MetaCell label="SOURCE" value={sourceLabel} />
          <MetaCell label="CREATED BY" value={createdByValue} />
          {isTemplatedSurvey && (
            <>
              <MetaCell
                label="TEMPLATE"
                value={
                  template ? `${template.name} · ${template.category}` : ""
                }
              />
              <div />
            </>
          )}
          <MetaCell
            label="DESCRIPTION"
            value={diagnostic.description ?? ""}
            fullWidth
          />
        </dl>
      </div>
      {isTemplatedSurvey && template && (
        <div className={styles.templateCard}>
          <div className={styles.templateCardHeader}>
            <span className={styles.templateCardDot} aria-hidden="true" />
            <span className={styles.templateCardMeta}>
              FROM THE {template.category} · {template.name}
            </span>
          </div>
          <p className={styles.templateCardDesc}>{template.description}</p>
          {template.whenToUse && (
            <p className={styles.templateCardWhenToUse}>
              <span className={styles.templateCardWhenToUseLabel}>
                When to use:
              </span>{" "}
              {template.whenToUse}
            </p>
          )}
          <div className={styles.templateCardActions}>
            {template.questionBankUrl && (
              <a
                href={template.questionBankUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={mergeClasses(
                  styles.templateCardLink,
                  styles.templateCardLinkPrimary,
                )}
              >
                <AttachRegular aria-hidden="true" />
                Question Bank
              </a>
            )}
            {template.guideUrl && (
              <a
                href={template.guideUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={mergeClasses(
                  styles.templateCardLink,
                  styles.templateCardLinkPrimary,
                )}
              >
                <AttachRegular aria-hidden="true" />
                Guide
              </a>
            )}
          </div>
        </div>
      )}
      {isCustomSurveyWithUpload && (
        <div className={styles.templateCard}>
          <div className={styles.templateCardHeader}>
            <span className={styles.templateCardDot} aria-hidden="true" />
            <span className={styles.templateCardMeta}>
              CUSTOM QUESTION BANK
            </span>
          </div>
          <div className={styles.templateCardActions}>
            <a
              href={diagnostic.customQuestionBankUrl!}
              target="_blank"
              rel="noopener noreferrer"
              className={mergeClasses(
                styles.templateCardLink,
                styles.templateCardLinkPrimary,
              )}
              aria-label="Download custom question bank (opens in new tab)"
            >
              <AttachRegular aria-hidden="true" />
              Download
            </a>
          </div>
        </div>
      )}
    </div>
  );
};
